import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, Save, X, Key, Building2, Mail, User, Phone, Shield } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

const ROLE_DESCRIPTIONS = {
  admin: {
    title: 'Super Admin',
    description: 'Full system access across all companies. Can manage users, view all data, and configure system settings.',
    permissions: ['Manage all users', 'Access all companies', 'View all submissions', 'System configuration']
  },
  hr_manager: {
    title: 'HR Manager',
    description: 'Company-specific HR management. Can manage employees and view company-specific data.',
    permissions: ['Manage company employees', 'View company submissions', 'Approve/reject submissions', 'Generate reports']
  },
  employee: {
    title: 'Employee',
    description: 'Basic user access for submitting forms and viewing personal data.',
    permissions: ['Submit registration form', 'View personal profile', 'Upload documents', 'Track submission status']
  }
};

export default function AdminUserCreationForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editUserId = searchParams?.get('edit');
  const isEditMode = Boolean(editUserId);

  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    company_id: '',
    role: 'employee',
    password: '',
    confirmPassword: '',
    is_active: true,
    send_email: true
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchCompanies();
    if (isEditMode) {
      fetchUserData();
    }
  }, []);

  const fetchCompanies = async () => {
    try {
      const { data, error } = await supabase?.from('companies')?.select('id, name, type')?.order('name');

      if (error) throw error;
      setCompanies(data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', editUserId)?.single();

      if (error) throw error;

      setFormData({
        full_name: data?.full_name || '',
        email: data?.email || '',
        phone_number: data?.phone_number || '',
        company_id: data?.company_id || '',
        role: data?.role || 'employee',
        password: '',
        confirmPassword: '',
        is_active: data?.is_active ?? true,
        send_email: false
      });
    } catch (error) {
      console.error('Error fetching user data:', error);
      alert('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.full_name?.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData?.phone_number?.trim()) {
      newErrors.phone_number = 'Phone number is required';
    }

    if (!formData?.company_id) {
      newErrors.company_id = 'Company assignment is required';
    }

    if (!isEditMode) {
      if (!formData?.password) {
        newErrors.password = 'Password is required';
      } else if (formData?.password?.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }

      if (formData?.password !== formData?.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    } else if (formData?.password) {
      if (formData?.password?.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
      if (formData?.password !== formData?.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      if (isEditMode) {
        // Update existing user
        const { error: updateError } = await supabase?.from('user_profiles')?.update({
            full_name: formData?.full_name,
            phone_number: formData?.phone_number,
            company_id: formData?.company_id,
            role: formData?.role,
            is_active: formData?.is_active
          })?.eq('id', editUserId);

        if (updateError) throw updateError;

        // Update password if provided
        if (formData?.password) {
          const { error: passwordError } = await supabase?.auth?.admin?.updateUserById(
            editUserId,
            { password: formData?.password }
          );
          
          if (passwordError) throw passwordError;
        }

        alert('User updated successfully!');
      } else {
        // Create new user
        const { data: authData, error: authError } = await supabase?.auth?.signUp({
          email: formData?.email,
          password: formData?.password,
          options: {
            data: {
              full_name: formData?.full_name,
              role: formData?.role,
              company_id: formData?.company_id,
              phone_number: formData?.phone_number,
              is_active: formData?.is_active
            },
            emailRedirectTo: `${window.location?.origin}/auth/callback`
          }
        });

        if (authError) throw authError;

        alert('Admin user created successfully!');
      }

      navigate('/admin-user-management-system');
    } catch (error) {
      console.error('Error saving user:', error);
      alert(error?.message || 'Failed to save user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const selectedRole = ROLE_DESCRIPTIONS?.[formData?.role] || ROLE_DESCRIPTIONS?.employee;

  if (loading && isEditMode) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEditMode ? 'Edit' : 'Create'} Admin User - CorperConnect</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/admin-user-management-system')}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to User Management
            </button>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEditMode ? 'Edit Admin User' : 'Create New Admin User'}
            </h1>
            <p className="mt-2 text-gray-600">
              {isEditMode 
                ? 'Update user information and permissions' 
                : 'Add a new administrative user to the CorperConnect system'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Basic Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Full Name *
                  </label>
                  <Input
                    value={formData?.full_name}
                    onChange={(e) => handleChange('full_name', e?.target?.value)}
                    placeholder="Enter full name"
                    error={errors?.full_name}
                  />
                  {errors?.full_name && (
                    <p className="mt-1 text-sm text-red-600">{errors?.full_name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    value={formData?.email}
                    onChange={(e) => handleChange('email', e?.target?.value)}
                    placeholder="user@example.com"
                    disabled={isEditMode}
                    error={errors?.email}
                  />
                  {errors?.email && (
                    <p className="mt-1 text-sm text-red-600">{errors?.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Phone Number *
                  </label>
                  <Input
                    value={formData?.phone_number}
                    onChange={(e) => handleChange('phone_number', e?.target?.value)}
                    placeholder="+234 800 000 0000"
                    error={errors?.phone_number}
                  />
                  {errors?.phone_number && (
                    <p className="mt-1 text-sm text-red-600">{errors?.phone_number}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building2 className="w-4 h-4 inline mr-2" />
                    Company Assignment *
                  </label>
                  <Select
                    value={formData?.company_id}
                    onChange={(value) => handleChange('company_id', value)}
                    options={[
                      { value: '', label: 'Select a company' },
                      ...companies?.map(c => ({ value: c?.id, label: c?.name }))
                    ]}
                    error={errors?.company_id}
                  />
                  {errors?.company_id && (
                    <p className="mt-1 text-sm text-red-600">{errors?.company_id}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Role Selection */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                <Shield className="w-5 h-5 inline mr-2" />
                Role & Permissions
              </h2>

              <div className="space-y-4">
                {Object.entries(ROLE_DESCRIPTIONS)?.map(([roleKey, roleInfo]) => (
                  <label
                    key={roleKey}
                    className={`block p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      formData?.role === roleKey
                        ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start">
                      <input
                        type="radio"
                        name="role"
                        value={roleKey}
                        checked={formData?.role === roleKey}
                        onChange={(e) => handleChange('role', e?.target?.value)}
                        className="mt-1"
                      />
                      <div className="ml-3 flex-1">
                        <div className="font-semibold text-gray-900">{roleInfo?.title}</div>
                        <p className="text-sm text-gray-600 mt-1">{roleInfo?.description}</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {roleInfo?.permissions?.map((permission, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {permission}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Security Settings */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                <Key className="w-5 h-5 inline mr-2" />
                Security Settings
              </h2>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isEditMode ? 'New Password (optional)' : 'Password *'}
                    </label>
                    <Input
                      type="password"
                      value={formData?.password}
                      onChange={(e) => handleChange('password', e?.target?.value)}
                      placeholder="Enter password"
                      error={errors?.password}
                    />
                    {errors?.password && (
                      <p className="mt-1 text-sm text-red-600">{errors?.password}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Must be at least 8 characters long
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {isEditMode ? 'Confirm New Password' : 'Confirm Password *'}
                    </label>
                    <Input
                      type="password"
                      value={formData?.confirmPassword}
                      onChange={(e) => handleChange('confirmPassword', e?.target?.value)}
                      placeholder="Confirm password"
                      error={errors?.confirmPassword}
                    />
                    {errors?.confirmPassword && (
                      <p className="mt-1 text-sm text-red-600">{errors?.confirmPassword}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData?.is_active}
                    onChange={(e) => handleChange('is_active', e?.target?.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                    Account is active (user can sign in)
                  </label>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin-user-management-system')}
              >
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : (isEditMode ? 'Update User' : 'Create User')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}